import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { IChatConversationMessage } from '@fi-shared/fi-chat/fi-chat';
import { FiChatService } from '@fi-shared/fi-chat/fi-chat.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { AppTextService } from '@fi-shared/app-text';
import { FiContenteditableComponent } from '@fi-shared/fi-contenteditable/fi-contenteditable.component';
import { ITextEditorLinkAttachment } from '@fi-shared/fi-text-editor/fi-text-editor';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiEmojiService } from '@fi-shared/fi-emoji-button/fi-emoji.service';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { FiContenteditableService } from '@fi-shared/fi-contenteditable/fi-contenteditable.service';
import { FiMessageEditorComponent } from './message-editor/message-editor.component';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';

@Component({
    selector: 'fi-chat-conversation-edit-message',
    templateUrl: 'fi-chat-conversation-edit-message.component.html',
    styleUrls: ['./fi-chat-conversation-edit-message.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-chat-conversation-edit-message' },
    standalone: false,
})
export class FiChatConversationEditMessageComponent implements OnInit {
    @Input() conversationMessage: IChatConversationMessage;
    @Input() sourceMessage: IChatConversationMessage;
    @Input() isMessageInputFocus: boolean = false;
    @Input() isEditMode: boolean = false;

    @Output() saveClick: EventEmitter<IChatConversationMessage> = new EventEmitter<IChatConversationMessage>();
    @Output() cancelEditMode: EventEmitter<void> = new EventEmitter();
    @Output() onContentChange: EventEmitter<void> = new EventEmitter();
    @Output() scrollToMessage = new EventEmitter<IChatConversationMessage>();

    @ViewChild('fiMessageEditorComponent', { static: true })
    fiMessageEditorComponent: FiMessageEditorComponent;
    protected isGiphyOverlayShown$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected isMessageSendProcess: boolean = false;
    protected onContentChange$: Subject<void> = new Subject<void>();
    private readonly conversationContainerOpeningAnimationDurationMS = 500;
    protected initialConversationMessage: IChatConversationMessage;

    constructor(
        private fiChatService: FiChatService,
        private fiChatHelperService: FiChatHelperService,
        private cdr: ChangeDetectorRef,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private detectDeviceService: DetectDevicesService,
        private fiEmojiService: FiEmojiService,
        private contenteditableService: FiContenteditableService,
        private fiUserDataService: FiUserDataService,
    ) {}

    get fiContenteditableComponent(): FiContenteditableComponent {
        return this.fiMessageEditorComponent.fiContenteditableComponent;
    }

    get getMessageInputEl(): HTMLElement {
        return this.fiContenteditableComponent.contenteditableElRef.nativeElement;
    }

    public ngOnInit(): void {
        if (this.isMessageInputFocus) {
            setTimeout(() => {
                this.focusInputMessage();
            }, this.conversationContainerOpeningAnimationDurationMS);
        }

        this.fiChatService.resetSendMessageProcess$.subscribe(() => (this.isMessageSendProcess = false));

        if (this.isEditMode) {
            this.setInitialMessageData();
        }
    }

    protected isOutgoingMessage(): boolean {
        return this.conversationMessage?.CreatedByUserId === this.fiUserDataService.getUserId();
    }

    protected scrollToMessageClick(message: IChatConversationMessage): void {
        this.scrollToMessage.emit(message);
    }

    /*
    private safeTagsDebug(str: string): string {
        return fiTextToHtml(str);
    }
    */

    protected contentChange(): void {
        this.onContentChange$.next();
        if (isDefined(this.onContentChange)) {
            this.onContentChange.emit();
        }
    }

    protected toggleGiphyOverlay(isShown: boolean): void {
        setTimeout(() => {
            this.isGiphyOverlayShown$.next(isShown);
            if (this.isMobilePhone() && !isShown) this.focusInputMessage();
        });
    }

    protected cancelEditing(): void {
        if (!this.isEditMode) return;

        if (this.isMessageChanged()) {
            this.openCancelEditConfirmationDialog();
        } else {
            this.cancelEditMode.emit();
        }
    }

    protected handleEnterKeyPress(): void {
        if (this.isSaveDisabled() || this.isMessageSendProcess) return;

        setTimeout(() => {
            // we need to wait for the fiReplaceWithLinks directive to work
            this.isMessageSendProcess = true;
            this.saveConversationMessage();
        }, 200);
    }

    protected isSaveDisabled(): boolean {
        return !(
            !!this.trimMessageContent().length ||
            this.fiChatHelperService.hasAttachments(this.conversationMessage) ||
            isDefined(this.sourceMessage)
        );
    }

    protected saveConversationMessage(): void {
        this.conversationMessage.Content = this.fiEmojiService.parseTextToEmoji(this.trimMessageContent());

        this.saveClick.emit(this.conversationMessage);
        this.focusInputMessage();
    }

    protected onGiphyItemClick($event: ITextEditorLinkAttachment): void {
        let newConversationMessage: IChatConversationMessage;
        if (this.isEditMode) {
            newConversationMessage = cloneDeep(this.conversationMessage);
            newConversationMessage.Content = '';
        } else {
            newConversationMessage = this.fiChatHelperService.getNewConversationMessageInstance();
        }

        newConversationMessage.Links.push($event);
        this.toggleGiphyOverlay(false);
        this.saveClick.emit(newConversationMessage);
    }

    protected addMessageAttachments($event: IMediaBankFileReference[]): void {
        this.conversationMessage.MediaBankFileReferences = [
            ...this.conversationMessage.MediaBankFileReferences,
            ...$event,
        ];
        this.contentChange();
        this.fiMessageEditorComponent.detectChanges();
    }

    protected isMobilePhone(): boolean {
        return this.detectDeviceService.isMobilePhone();
    }

    private trimMessageContent(): string {
        return this.getMessageInputEl.innerHTML
            .replace(/<br>/g, '\n')
            .replace(/&nbsp;/g, ' ')
            .trim()
            .replace(/\n/g, '<br>');
    }

    private openCancelEditConfirmationDialog(): void {
        setTimeout(() => {
            this.fiDialogsService
                .confirm({
                    headerText: this.appTextService.get('common.discard_draft_dialog_text'),
                    messageText: this.appTextService.get('chat.conversation_cancel_edit_message'),
                    yesButtonText: this.appTextService.get('common.discard'),
                })
                .onYesButtonClick(() => this.cancelEditMode.emit());
        });
    }

    private setInitialMessageData(): void {
        this.initialConversationMessage = cloneDeep(this.conversationMessage);
    }

    private isMessageChanged(): boolean {
        return (
            this.initialConversationMessage.Content !== this.conversationMessage.Content ||
            JSON.stringify(this.initialConversationMessage.MediaBankFileReferences) !==
                JSON.stringify(this.conversationMessage.MediaBankFileReferences)
        );
    }

    private focusInputMessage(): void {
        const contentEditableElement = this.getMessageInputEl;
        contentEditableElement.focus();
        this.contenteditableService.placeCaretAtEnd(contentEditableElement);
    }
}
